import { BasicReference, Beacon, mapRef, sequentialPairs, strictEqual, subscribeWithRetention, } from "@knyt/artisan";
import { actionCreatorFactory, isAction, } from "./action/fsa.js";
import { createAccessor, } from "./createAccessor.js";
import { logDispatch } from "./logDispatch.js";
/**
 * An `Reference` that can dispatch actions to update its state.
 */
export class Store extends BasicReference {
    /**
     * The current state of the store.
     *
     * @deprecated Use `value` instead.
     * @internal scope: workspace
     */
    get state() {
        return this.value;
    }
    /**
     * An observable that emits actions that have been dispatched to the store.
     *
     * @remarks
     *
     * This is intentionally not a `Reference`, but a `Beacon`,
     * because references emit the current value immediately upon subscription,
     * while beacons only emit a stream of values.
     *
     * We don't want to emit "the last dispatched action", we want to subscribe
     * future dispatched actions.
     */
    #actionSignals = Beacon.withEmitter();
    /**
     * An observable that emits actions that have been dispatched to the store.
     *
     * @public
     */
    get action$() {
        return this.#actionSignals.beacon;
    }
    /**
     * Enable debug logging for dispatches.
     *
     * @public
     */
    debug;
    /**
     * The action creator factory used to create action creators.
     */
    #createActionCreator;
    constructor(initialState, options = {}) {
        const { onUpdate, comparator, debug, actionPrefix } = options;
        super({
            initialValue: initialState,
            onUpdate,
            comparator,
        });
        this.#createActionCreator = actionCreatorFactory(actionPrefix);
        this.debug = debug ?? false;
    }
    #actionReducers = {};
    /**
     * Dispatch an action to update the store's state.
     *
     * @detachable
     * @public
     */
    /*
     * ### Private Remarks
     *
     * The method is not readonly, so that it may be overridden.
     * It is also an arrow function so that it may be passed
     * around as a callback without losing its context.
     *
     * All side effects must be queued as microtasks.
     *
     * No batching is performed; i.e. all reducers and subscribers receive every action/update.
     */
    dispatch = (input) => {
        if (typeof input === "function") {
            // Type cast to `any` avoids unhelpful type error
            // for the `Dispatch` overload return types.
            return input(this);
        }
        this.#dispatchAction(input);
    };
    #dispatchAction(action) {
        const prevState = this.value;
        let nextState = this.value;
        if (isAction(action)) {
            const matchingReducer = this.#actionReducers[action.type];
            if (matchingReducer) {
                nextState = matchingReducer.apply(this, [nextState, action]);
            }
        }
        if (this.reduce) {
            nextState = this.reduce(nextState, action);
        }
        this.value = nextState;
        // All store side effects are asynchronous,
        // so they must be queued as microtasks.
        queueMicrotask(() => {
            // NOTE: This is not a `Reference`, but a `Beacon`.
            // `Beacon` emitter methods are synchronous, so we
            // must emit after a microtask to make the side effect
            // asynchronous.
            this.#actionSignals.next(action);
            if (this.debug) {
                logDispatch({
                    action,
                    nextState,
                    prevState,
                });
            }
        });
    }
    /**
     * Subscribe to changes to a selected value from the store's state.
     * The listener will only be called when the selected value changes.
     *
     * @deprecated Use `ref` instead.
     * @internal scope: workspace
     */
    #subscribeToSelector(listener, selector, comparator = strictEqual) {
        const initialValue = selector(this.value);
        const observer = sequentialPairs(initialValue, ([previousValue, currentValue]) => listener(currentValue, previousValue));
        const selectedState$ = this.ref(selector, comparator);
        return subscribeWithRetention(selectedState$, observer);
    }
    /**
     * Bind an action creator to the store's dispatch method..
     *
     * @deprecated Use `createAction` or `createActions` instead.
     * @internal scope: workspace
     */
    // TODO: Make this private and only expose `createAction` and `createActions`.
    bindActionCreator(actionCreator) {
        return (payload) => this.dispatch(actionCreator(payload));
    }
    #createBoundAction(type) {
        const actionCreator = this.#createActionCreator(type);
        const boundActionCreator = this.bindActionCreator(actionCreator);
        return Object.assign(boundActionCreator, {
            match: actionCreator.match,
            type: actionCreator.type,
        });
    }
    /**
     * Create an action creator that can be dispatched to
     * update the store's state using the given reducer.
     *
     * @public
     */
    // TODO: Consider making the name optional, and generate a random unique name if not provided.
    createAction(type, reducer) {
        this.#actionReducers[type] = reducer;
        return this.#createBoundAction(type);
    }
    /**
     * Create a set of action creators that can be dispatched to update the store's state.
     *
     * @public
     */
    createActions(reducers) {
        const actionCreators = {};
        for (const type in reducers) {
            actionCreators[type] = this.createAction(type, reducers[type]);
        }
        return actionCreators;
    }
    /**
     * Define a selector that selects a value from the store's state.
     *
     * @detachable
     *
     * @deprecated
     */
    /*
     * ### Private Remarks
     *
     * This is a simple no-op method for convenience, that helps
     * to define the type of the selector.
     */
    defineSelector(selector) {
        return selector;
    }
    /**
     * A set of selectors for each property in the store's state.
     *
     * @public
     */
    select = new Proxy({}, {
        get(_target, property) {
            return (state) => state[property];
        },
    });
    /**
     * Create an accessor that provides access to selected values from the store's state.
     *
     * @public
     */
    createAccessor(input) {
        return createAccessor(this, input);
    }
    /**
     * Define a selector that selects a property from the store's state.
     *
     * @detachable
     *
     * @deprecated Use `selectors` instead.
     */
    propertySelector = (propertyName) => {
        return (state) => state[propertyName];
    };
    /**
     * Create a subscription provider that creates subscriptions for a selected value from the store's state.
     *
     * @deprecated Use `ref` instead.
     * @internal scope: workspace
     */
    createSubscriptionFactory(selector, comparator) {
        return (listener) => {
            return this.#subscribeToSelector(listener, selector, comparator);
        };
    }
    /**
     * Create a  reference that observes a selected value from the store's state.
     *
     * @deprecated Use `createAccessor` instead.
     * @internal scope: workspace
     * TODO: Make this private
     */
    /*
     * ### Private Remarks
     *
     * The method doesn't have the `onUpdate` parameter,
     * because there should be no need to create side
     * effects in a store, and consumers should use the
     * observable's `subscribe` method instead.
     */
    ref(selector, comparator) {
        return mapRef({
            origin: this,
            transform: selector,
            comparator,
        });
    }
}
